import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStrictContext } from '@/lib/get-strict-context';
import { useAuth } from '@/features/auth/providers/AuthProvider';
import useGetUserRole from '@/features/user/hooks/useGetUserRole';
import useOnboardingStatus from '@/features/onboarding/hooks/useOnboardingStatus';
import { getChaptersForRole } from '@/features/onboarding/chapters';
import { welcomeTour } from '@/features/onboarding/welcome-tour';
import { OnboardingDialog } from '@/features/onboarding/components/OnboardingDialog';
import type { OnboardingChapter } from '@/features/onboarding/types';

interface OnboardingContextValue {
  startTour: () => void;
}

const [OnboardingContextProvider, useOnboarding] =
  getStrictContext<OnboardingContextValue>('OnboardingProvider');

interface OnboardingProviderProps {
  /** Whether unseen slides may open on their own. The tour stays replayable either way. */
  enabled: boolean;
  children: React.ReactNode;
}

export function OnboardingProvider({ enabled, children }: OnboardingProviderProps) {
  const { user } = useAuth();
  const startTourRef = useRef<(() => void) | null>(null);
  const value = useMemo(() => ({ startTour: () => startTourRef.current?.() }), []);

  return (
    <OnboardingContextProvider value={value}>
      {children}
      {/* Keyed per user so the role and seen hooks start fresh instead of reporting the previous user's state. */}
      {user && (
        <OnboardingController
          key={user.uid}
          userId={user.uid}
          autoOpen={enabled}
          startTourRef={startTourRef}
        />
      )}
    </OnboardingContextProvider>
  );
}

interface Deck {
  key: number;
  chapters: OnboardingChapter[];
}

interface OnboardingControllerProps {
  userId: string;
  autoOpen: boolean;
  startTourRef: React.RefObject<(() => void) | null>;
}

/** Drops slides this person has already closed, and drops a chapter that has none left. */
function unseenChapters(
  chapters: OnboardingChapter[],
  seen: Record<string, number>
): OnboardingChapter[] {
  return chapters
    .map(chapter => ({
      ...chapter,
      slides: chapter.slides.filter(slide => !seen[slide.id]),
    }))
    .filter(chapter => chapter.slides.length > 0);
}

function OnboardingController({ userId, autoOpen, startTourRef }: OnboardingControllerProps) {
  const { role, isLoading: roleLoading } = useGetUserRole(userId);
  const { seen, isLoading: seenLoading, error, markSeen } = useOnboardingStatus(userId);
  const [deck, setDeck] = useState<Deck | null>(null);
  const [open, setOpen] = useState(false);
  const hasAutoOpened = useRef(false);

  const chapters = useMemo(() => getChaptersForRole(role ?? 'user'), [role]);

  const openDeck = useCallback((deckChapters: OnboardingChapter[]) => {
    if (deckChapters.length === 0) return;
    setDeck(prev => ({ key: (prev?.key ?? 0) + 1, chapters: deckChapters }));
    setOpen(true);
  }, []);

  useEffect(() => {
    // Checked once per session, so a failed seen-write can't reopen the deck in a loop.
    if (!autoOpen || roleLoading || seenLoading || error || hasAutoOpened.current) return;
    hasAutoOpened.current = true;
    openDeck(unseenChapters(chapters, seen));
  }, [autoOpen, roleLoading, seenLoading, error, chapters, seen, openDeck]);

  useEffect(() => {
    startTourRef.current = () =>
      openDeck(chapters.filter(chapter => chapter.id === welcomeTour.id));
    return () => {
      startTourRef.current = null;
    };
  }, [startTourRef, chapters, openDeck]);

  const close = useCallback(() => {
    setOpen(false);
    if (!deck) return;
    markSeen(deck.chapters.flatMap(chapter => chapter.slides.map(slide => slide.id))).catch(err =>
      console.error('Error saving onboarding status:', err)
    );
  }, [deck, markSeen]);

  if (!deck) return null;

  return <OnboardingDialog key={deck.key} open={open} chapters={deck.chapters} onClose={close} />;
}

export { useOnboarding };
